import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getDatabase, ref, push, onValue, query, orderByChild } from 'firebase/database';
import { app } from '../../firebase';

const TriviaContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: #333;
  font-family: 'Lora', serif;
  font-size: 2.2rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-family: 'EB Garamond', serif;
  font-size: 1.3rem;
  margin-bottom: 2rem;
`;

const QuestionCard = styled.div`
  background: white;
  padding: 3rem 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 600px;
  text-align: center;
  margin-bottom: 2rem;
`;

const QuestionText = styled.h2`
  color: #333;
  font-family: 'Lora', serif;
  margin-bottom: 2.5rem;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 1.4;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Option = styled.button`
  padding: 1.2rem 2rem;
  border: 2px solid #eee;
  border-radius: 8px;
  background: white;
  color: #666;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'EB Garamond', serif;
  text-align: left;

  &:hover:not(:disabled) {
    border-color: #333;
    color: #333;
    transform: translateY(-2px);
  }

  ${props => props.selected && `
    border-color: #333;
    color: #333;
    background: ${props.isCorrect === true ? '#e8f5e9' : 
                 props.isCorrect === false ? '#ffebee' : 'white'};
  `}

  &:disabled {
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  padding: 1rem 2.5rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'EB Garamond', serif;
  margin: 0 0.5rem;

  &:hover:not(:disabled) {
    background: #444;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: #666;
  
  &:hover:not(:disabled) {
    background: #777;
  }
`;

const ProgressIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const ProgressDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.completed ? '#333' : props.current ? '#666' : '#eee'};
  transition: all 0.3s ease;
`;

const ResultCard = styled.div`
  background: white;
  padding: 3rem 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const FinalScore = styled.h2`
  color: #333;
  font-family: 'Lora', serif;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const ScoreDetail = styled.p`
  color: #666;
  font-family: 'EB Garamond', serif;
  font-size: 1.3rem;
  margin-bottom: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const CorrectAnswerText = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f8f8;
  border-radius: 4px;
  color: #666;
  font-family: 'EB Garamond', serif;
  font-size: 1.1rem;
`;

const CalendarContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  background: white;
  padding: 1rem;

  @media (max-width: 480px) {
    padding: 0.5rem;
    max-width: 100%;
  }
`;

const MonthSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  max-width: 400px;
  margin: 0 auto 1rem auto;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.3rem;
    max-width: 100%;
  }
`;

const MonthButton = styled.button`
  padding: 0.5rem;
  border: 1px solid #eee;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'EB Garamond', serif;
  border-radius: 4px;
  font-size: 1.1rem;

  @media (max-width: 480px) {
    padding: 0.4rem;
    font-size: 1rem;
  }

  &:hover {
    background: #f5f5f5;
  }

  ${props => props.selected && `
    background: #333;
    color: white;
    border-color: #333;

    &:hover {
      background: #444;
    }
  `}

  &:disabled {
    cursor: ${props => props.selected ? 'default' : 'not-allowed'};
    opacity: ${props => props.selected ? 1 : 0.5};
  }
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const DayHeader = styled.div`
  text-align: center;
  font-family: 'EB Garamond', serif;
  font-size: 1rem;
  color: #666;
  padding: 0.5rem;

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.3rem;
  }
`;

const DateButton = styled.button`
  aspect-ratio: 1;
  border: 1px solid #eee;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'EB Garamond', serif;
  font-size: 1.1rem;

  @media (max-width: 480px) {
    font-size: 1rem;
  }

  &:hover {
    background: #f5f5f5;
  }

  ${props => props.selected && `
    background: ${props.isCorrect === true ? '#e8f5e9' : 
                props.isCorrect === false ? '#ffebee' : '#333'};
    color: ${props.isCorrect !== undefined ? '#333' : 'white'};
    border-color: ${props.isCorrect === true ? '#4caf50' : 
                   props.isCorrect === false ? '#f44336' : '#333'};
  `}

  &:disabled {
    cursor: ${props => props.selected ? 'default' : 'not-allowed'};
    opacity: ${props => props.selected ? 1 : 0.5};
  }
`;

const NumberInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
`;

const NumberInput = styled.input`
  padding: 1rem;
  font-size: 1.2rem;
  width: 200px;
  text-align: center;
  border: 2px solid #eee;
  border-radius: 8px;
  font-family: 'EB Garamond', serif;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #333;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 0.8rem 1.5rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'EB Garamond', serif;

  &:hover {
    background: #444;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ResultMessage = styled.div`
  margin-top: 1rem;
  font-family: 'EB Garamond', serif;
  color: ${props => props.isCorrect ? '#4caf50' : '#f44336'};
  font-weight: 500;
  font-size: 1.2rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem 2rem;
  border-top: 1px solid #eee;
  flex-shrink: 0;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 2rem;
  max-height: 400px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  font-family: 'Lora', serif;
  font-size: 1.8rem;
  text-align: center;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #eee;
  border-radius: 4px;
  font-size: 1.1rem;
  font-family: 'EB Garamond', serif;
  margin-bottom: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const CancelButton = styled.button`
  padding: 0.8rem 1.5rem;
  background: #666;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'EB Garamond', serif;

  &:hover {
    background: #777;
  }
`;

const LeaderboardList = styled.div`
  text-align: left;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const LeaderboardItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.8rem;
  border-bottom: 1px solid #eee;
  font-family: 'EB Garamond', serif;
  font-size: 1.1rem;

  &:first-child {
    background: #f8f8f8;
    font-weight: 500;
  }
`;

const questions = [
  {
    question: "Which of the following U.S. cities have David & Hana NOT traveled to together?",
    options: [
      "Asheville",
      "Charleston",
      "Chicago",
      "Miami"
    ],
    correctAnswer: 3,
    correctAnswerText: "Miami"
  },
  {
    question: "Which concerts have David and Hana NOT seen together?",
    options: [
      "Black Crowes",
      "Sum 41",
      "Elton John",
      "Nickelback",
      "Avril Lavigne",
      "The Script"
    ],
    correctAnswer: 3,
    correctAnswerText: "Nickleback"
  },
  {
    question: "What anniversary date do Hana and David celebrate?",
    options: [
      "June 2nd",
      "October 10th",
      "July 5th",
      "September 23rd",
      "May 31st"
    ],
    correctAnswer: 2,
    correctAnswerText: "July 5th"
  },
  {
    question: "Hana and David have the same favorite food - what is it?",
    options: [
      "Sushi",
      "Chicken Wings",
      "Tacos",
      "Pizza"
    ],
    correctAnswer: 0,
    correctAnswerText: "Sushi"
  },
  {
    question: "Which sports teams do Hana and David both root for?",
    options: [
      "LIU sharks",
      "Mets",
      "Yankees",
      "Red Sox"
    ],
    correctAnswer: 0,
    correctAnswerText: "LIU sharks"
  },
  {
    question: "On what holiday did Hana and David meet?",
    options: [
      "Chanukah",
      "Thanksgiving",
      "July 4th",
      "Passover",
      "Flag Day"
    ],
    correctAnswer: 0,
    correctAnswerText: "Chanukah"
  },
  {
    question: "What is David's nickname for Hana?",
    options: [
      "Beni-Hana",
      "Hana-Banana",
      "H Money",
      "Hanoose"
    ],
    correctAnswer: 3,
    correctAnswerText: "Hanoose"
  },
  {
    question: "What is the height difference between Hana and David?",
    options: [
      "5 inches",
      "8 inches",
      "10 inches",
      "12 inches",
      "14 inches"
    ],
    correctAnswer: 3,
    correctAnswerText: "12 inches"
  },
  {
    question: "What's Hana and David's favorite YouTube Channel to watch before bed?",
    options: [
      "Mr. Beast",
      "Outdoor Boys",
      "PewDiePie",
      "Bob Ross"
    ],
    correctAnswer: 1,
    correctAnswerText: "Outdoor Boys"
  },
];

const Calendar = ({ onSelectDate, selectedDate, showResult, correctDate }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (month) => {
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonth[month];
  };

  const getFirstDayOfMonth = (month) => {
    return new Date(2024, month, 1).getDay();
  };

  const handleDateSelect = (day) => {
    if (!selectedMonth && selectedMonth !== 0) return;
    onSelectDate({ month: selectedMonth, day });
  };

  const isDateSelected = (month, day) => {
    return selectedDate && selectedDate.month === month && selectedDate.day === day;
  };

  const isCorrectAnswer = (month, day) => {
    if (!showResult || !correctDate) return undefined;
    return month === correctDate.month && day === correctDate.day;
  };

  const isCorrectSelection = selectedDate && 
    selectedDate.month === correctDate.month && 
    selectedDate.day === correctDate.day;

  return (
    <CalendarContainer>
      <MonthSelector>
        {months.map((month, index) => (
          <MonthButton
            key={month}
            onClick={() => !showResult && setSelectedMonth(index)}
            selected={selectedMonth === index || (showResult && correctDate.month === index)}
            disabled={showResult}
          >
            {month}
          </MonthButton>
        ))}
      </MonthSelector>
      {(selectedMonth !== null || showResult) && (
        <>
          <DaysGrid>
            {days.map(day => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
            {Array.from({ length: getFirstDayOfMonth(selectedMonth || correctDate.month) }, (_, i) => (
              <DateButton key={`empty-${i}`} disabled style={{ visibility: 'hidden' }} />
            ))}
            {Array.from({ length: getDaysInMonth(selectedMonth || correctDate.month) }, (_, i) => (
              <DateButton
                key={i + 1}
                onClick={() => !showResult && handleDateSelect(i + 1)}
                selected={isDateSelected(selectedMonth, i + 1) || (showResult && isCorrectAnswer(correctDate.month, i + 1))}
                isCorrect={isCorrectAnswer(selectedMonth, i + 1)}
                disabled={showResult}
              >
                {i + 1}
              </DateButton>
            ))}
          </DaysGrid>
          {showResult && !isCorrectSelection && (
            <CorrectAnswerText>
              The correct date is {months[correctDate.month]} {correctDate.day}
            </CorrectAnswerText>
          )}
        </>
      )}
    </CalendarContainer>
  );
};

const NumberQuestion = ({ onSubmit, showResult, selectedAnswer, isCorrect, correctAnswer, unit }) => {
  const [inputValue, setInputValue] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!inputValue) return;
    setHasSubmitted(true);
    onSubmit(parseInt(inputValue, 10));
  };

  return (
    <NumberInputContainer>
      <NumberInput
        type="number"
        inputmode="numeric"
        pattern="[0-9]*"
        value={showResult ? selectedAnswer : inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        disabled={showResult}
        placeholder={`Number of ${unit}`}
      />
      {!showResult && (
        <SubmitButton 
          onClick={handleSubmit}
          disabled={!inputValue || hasSubmitted}
        >
          Submit Answer
        </SubmitButton>
      )}
      {showResult && (
        <>
          <ResultMessage isCorrect={isCorrect}>
            {isCorrect 
              ? "Correct!"
              : "Not quite!"}
          </ResultMessage>
          {!isCorrect && (
            <CorrectAnswerText>
              The correct answer is {correctAnswer} {unit}
            </CorrectAnswerText>
          )}
        </>
      )}
    </NumberInputContainer>
  );
};

const Trivia = () => {
  const [currentQuestion, setCurrentQuestion] = useState(-1); // -1 for welcome screen
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmittedScore, setHasSubmittedScore] = useState(false);

  useEffect(() => {
    // Fetch leaderboard data
    const db = getDatabase(app);
    const leaderboardRef = ref(db, 'triviaLeaderboard');
    const leaderboardQuery = query(leaderboardRef, orderByChild('score'));
    
    const unsubscribe = onValue(leaderboardQuery, (snapshot) => {
      const data = [];
      snapshot.forEach((childSnapshot) => {
        data.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      data.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.timestamp - b.timestamp;
      });
      setLeaderboard(data);
    });

    return () => unsubscribe();
  }, []);

  // Prevent background scrolling when modals are open
  useEffect(() => {
    if (showSubmitModal || showLeaderboardModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSubmitModal, showLeaderboardModal]);

  const startGame = () => {
    setCurrentQuestion(0);
  };

  const submitToLeaderboard = async () => {
    if (!playerName.trim()) return;
    
    setSubmitting(true);
    try {
      const db = getDatabase(app);
      const leaderboardRef = ref(db, 'triviaLeaderboard');
      await push(leaderboardRef, {
        name: playerName.trim(),
        score: score,
        totalQuestions: questions.length,
        timestamp: Date.now()
      });
      setShowSubmitModal(false);
      setPlayerName('');
      setHasSubmittedScore(true);
      // Automatically show leaderboard after submitting
      setShowLeaderboardModal(true);
    } catch (error) {
      console.error('Error submitting to leaderboard:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    const currentQ = questions[currentQuestion];
    
    if (currentQ.type === 'calendar') {
      if (answer.month === currentQ.correctDate.month && 
          answer.day === currentQ.correctDate.day) {
        setScore(score + 1);
      }
    } else if (currentQ.type === 'number') {
      if (answer === currentQ.correctAnswer) {
        setScore(score + 1);
      }
    } else {
      if (answer === currentQ.correctAnswer) {
        setScore(score + 1);
      }
    }
    setShowResult(true);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setGameComplete(true);
    }
  };

  const restartGame = () => {
    setCurrentQuestion(-1);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setGameComplete(false);
    setHasSubmittedScore(false);
  };

  // Welcome Screen
  if (currentQuestion === -1 && !gameComplete) {
    return (
      <TriviaContainer>
        <WelcomeSection>
          <Title>Couple Trivia</Title>
          <Subtitle>See how well you know David & Hana!</Subtitle>
          <ButtonGroup>
            <ActionButton onClick={startGame}>
              Start Game
            </ActionButton>
            <SecondaryButton onClick={() => setShowLeaderboardModal(true)}>
              View Leaderboard
            </SecondaryButton>
          </ButtonGroup>
        </WelcomeSection>

        {showLeaderboardModal && (
          <Modal onClick={() => setShowLeaderboardModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Leaderboard</ModalTitle>
              </ModalHeader>
              <ScrollableContent>
                <LeaderboardList>
                  {leaderboard.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666', fontFamily: 'EB Garamond, serif' }}>
                      No scores yet. Be the first to play!
                    </p>
                  ) : (
                    leaderboard.map((entry, index) => (
                      <LeaderboardItem key={entry.id}>
                        <span>#{index + 1} {entry.name}</span>
                        <span>{entry.score}/{entry.totalQuestions}</span>
                      </LeaderboardItem>
                    ))
                  )}
                </LeaderboardList>
              </ScrollableContent>
              <ModalFooter>
                <CancelButton onClick={() => setShowLeaderboardModal(false)}>
                  Close
                </CancelButton>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </TriviaContainer>
    );
  }

  // Game Complete Screen
  if (gameComplete) {
    return (
      <TriviaContainer>
        <ResultCard>
          <Title>Great Job!</Title>
          <FinalScore>You scored {score} out of {questions.length}!</FinalScore>
          <ScoreDetail>
            {score === questions.length && "Perfect score! You really know this couple!"}
            {score >= questions.length * 0.8 && score < questions.length && "Excellent! You know them well!"}
            {score >= questions.length * 0.6 && score < questions.length * 0.8 && "Not bad! Room for improvement though!"}
            {score < questions.length * 0.6 && "Looks like you need to get to know them better!"}
          </ScoreDetail>
          <ButtonGroup>
            {!hasSubmittedScore && (
              <ActionButton onClick={() => setShowSubmitModal(true)}>
                Submit Score
              </ActionButton>
            )}
            <SecondaryButton onClick={restartGame}>
              Play Again
            </SecondaryButton>
            <SecondaryButton onClick={() => setShowLeaderboardModal(true)}>
              Leaderboard
            </SecondaryButton>
          </ButtonGroup>
        </ResultCard>

        {showSubmitModal && (
          <Modal onClick={() => setShowSubmitModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Submit Score</ModalTitle>
              </ModalHeader>
              <ScrollableContent>
                <p style={{ textAlign: 'center', marginBottom: '1rem', fontFamily: 'EB Garamond, serif' }}>
                  Score: {score} out of {questions.length}
                </p>
                <NameInput
                  type="text"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  maxLength={50}
                />
              </ScrollableContent>
              <ModalFooter>
                <ModalButtons>
                  <SubmitButton 
                    onClick={submitToLeaderboard}
                    disabled={!playerName.trim() || submitting}
                  >
                    {submitting ? 'Saving...' : 'Submiting Score'}
                  </SubmitButton>
                  <CancelButton onClick={() => setShowSubmitModal(false)}>
                    Cancel
                  </CancelButton>
                </ModalButtons>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}

        {showLeaderboardModal && (
          <Modal onClick={() => setShowLeaderboardModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Leaderboard</ModalTitle>
              </ModalHeader>
              <ScrollableContent>
                <LeaderboardList>
                  {leaderboard.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666', fontFamily: 'EB Garamond, serif' }}>
                      No scores yet. Be the first to play!
                    </p>
                  ) : (
                    leaderboard.map((entry, index) => (
                      <LeaderboardItem key={entry.id}>
                        <span>#{index + 1} {entry.name}</span>
                        <span>{entry.score}/{entry.totalQuestions}</span>
                      </LeaderboardItem>
                    ))
                  )}
                </LeaderboardList>
              </ScrollableContent>
              <ModalFooter>
                <CancelButton onClick={() => setShowLeaderboardModal(false)}>
                  Close
                </CancelButton>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </TriviaContainer>
    );
  }

  // Game Play Screen
  return (
    <TriviaContainer>
      <ProgressIndicator>
        {questions.map((_, index) => (
          <ProgressDot
            key={index}
            completed={index < currentQuestion}
            current={index === currentQuestion}
          />
        ))}
      </ProgressIndicator>

      <QuestionCard>
        <QuestionText>{questions[currentQuestion].question}</QuestionText>
        
        {questions[currentQuestion].type === 'calendar' ? (
          <Calendar
            onSelectDate={handleAnswerSelect}
            selectedDate={selectedAnswer}
            showResult={showResult}
            correctDate={questions[currentQuestion].correctDate}
          />
        ) : questions[currentQuestion].type === 'number' ? (
          <NumberQuestion
            onSubmit={handleAnswerSelect}
            showResult={showResult}
            selectedAnswer={selectedAnswer}
            isCorrect={selectedAnswer === questions[currentQuestion].correctAnswer}
            correctAnswer={questions[currentQuestion].correctAnswer}
            unit={questions[currentQuestion].unit}
          />
        ) : (
          <OptionsContainer>
            {questions[currentQuestion].options.map((option, index) => (
              <Option
                key={index}
                onClick={() => !showResult && handleAnswerSelect(index)}
                selected={selectedAnswer === index || (showResult && index === questions[currentQuestion].correctAnswer)}
                isCorrect={showResult ? index === questions[currentQuestion].correctAnswer : undefined}
                disabled={showResult}
              >
                {option}
              </Option>
            ))}
          </OptionsContainer>
        )}

        {showResult && selectedAnswer !== questions[currentQuestion].correctAnswer && (
          <CorrectAnswerText>
            The correct answer is: {questions[currentQuestion].options[questions[currentQuestion].correctAnswer]}
          </CorrectAnswerText>
        )}

        {showResult && (
          <ActionButton onClick={handleNext}>
            {currentQuestion === questions.length - 1 ? 'Finish Game' : 'Next Question'}
          </ActionButton>
        )}
      </QuestionCard>
    </TriviaContainer>
  );
};

export default Trivia; 